import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RoleUserDTO } from '../../../core/models/role.model';
import { RoleService } from '../../../core/services/role.service';
import { RoleDeleteDialogComponent } from '../role-delete-dialog/role-delete-dialog.component';
import { RoleFormDialogComponent } from '../role-form-dialog/role-form-dialog.component';

import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';

export interface RoleDetailDialogData {
  role: RoleUserDTO;
  clientName?: string;
  isRealm: boolean;
}

@Component({
  selector: 'app-role-detail-dialog',
  standalone: true,
  imports: [MatIconModule, MatCardModule, MatChipsModule, MatButtonModule],
  templateUrl: './role-detail-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./role-detail-dialog.component.scss']
})
export class RoleDetailDialogComponent implements OnInit, OnDestroy {
  role: RoleUserDTO;
  clientName: string;
  isRealm: boolean;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<RoleDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RoleDetailDialogData,
    private roleService: RoleService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.role = data.role;
    this.clientName = data.clientName || '';
    this.isRealm = data.isRealm;
  }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Ouvrir le formulaire d'édition en modale
  editRole(): void {
    this.dialogRef.close('edit'); // On ferme le détail pour ouvrir l'édition
    // On ouvre le formulaire d'édition
    this.dialog.open(RoleFormDialogComponent, {
      width: '700px',
      disableClose: true,
      data: {
        mode: 'edit',
        role: this.role,
        clientId: this.isRealm ? undefined : this.role.containerId,
        isRealm: this.isRealm
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        // Rafraîchir la liste (le parent gérera le refresh)
        this.dialogRef.close('refresh');
      }
    });
  }

  // Ouvrir la boîte de suppression
  openDeleteDialog(): void {
    const dialogRef = this.dialog.open(RoleDeleteDialogComponent, {
      width: '450px',
      data: { role: this.role, clientName: this.clientName }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteRole();
      }
    });
  }

  private deleteRole(): void {
    const deleteObs = this.isRealm
      ? this.roleService.deleteRealmRole(this.role.name)
      : this.roleService.deleteClientRole(this.role.containerId!, this.role.name);
    deleteObs.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess(`Rôle "${this.role.name}" supprimé avec succès`);
          this.dialogRef.close('refresh');
        },
        error: (error) => {
          console.error('Erreur suppression:', error);
          this.showError('Erreur lors de la suppression du rôle');
        }
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  getRoleType(): string {
    return this.isRealm ? 'Rôle Realm' : 'Rôle Client';
  }

  getRoleTypeColor(): string {
    return this.isRealm ? '#45B56F' : '#2196f3';
  }

  getRoleTypeIcon(): string {
    return this.isRealm ? 'admin_panel_settings' : 'apps';
  }

  getInitials(): string {
    if (!this.role) return '?';
    const words = this.role.name.split(/[\s-_]+/);
    return words.length >= 2 ? (words[0][0] + words[1][0]).toUpperCase() : this.role.name.substring(0, 2).toUpperCase();
  }

  getAvatarColor(): string {
    const colors = ['#1abc9c','#2ecc71','#3498db','#9b59b6','#34495e','#16a085','#27ae60','#2980b9','#8e44ad','#2c3e50','#f39c12','#e67e22','#e74c3c','#95a5a6','#d35400'];
    let hash = 0;
    for (let i = 0; i < this.role.name.length; i++) hash = this.role.name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fermer', { duration: 3000, panelClass: ['success-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }
  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }
}